import { CountryCodes } from '@/services/commonService';
import i18n from '@/i18n';

/** User/profile country shape used for matching (e.g. user.countryCode). */
export interface UserCountryCode {
  code: string;
  abbreviation?: string;
}

// ================================
// CONSTANTS & CONFIGURATION
// ================================

// Mobile number length rules for the local number (digits only; country code is separate).
// Every country uses the same 9–11 range because the API does not yet return per-country limits.
export const UNIVERSAL_PHONE_VALIDATION = {
  minLength: 9,
  maxLength: 11,
  description: '9-11 digits',
} as const;

export const QUERY_CONFIG = {
  staleTime: 1000 * 60 * 60, // 1 hour
  gcTime: 1000 * 60 * 60 * 2, // 2 hours
  retry: 3,
  retryDelay: (attemptIndex: number) =>
    Math.min(1000 * 2 ** attemptIndex, 30000),
} as const;

// Fallback country data for when API fails
export const FALLBACK_COUNTRIES: CountryCodes[] = [
  {
    _id: 'fallback-india',
    name: 'India',
    code: '+91',
    abbreviation: 'IN',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    __v: 0,
  },
  {
    _id: 'fallback-usa',
    name: 'United States',
    code: '+1',
    abbreviation: 'US',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    __v: 0,
  },
  {
    _id: 'fallback-uk',
    name: 'United Kingdom',
    code: '+44',
    abbreviation: 'GB',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    __v: 0,
  },
  {
    _id: 'fallback-canada',
    name: 'Canada',
    code: '+1',
    abbreviation: 'CA',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    __v: 0,
  },
  {
    _id: 'fallback-australia',
    name: 'Australia',
    code: '+61',
    abbreviation: 'AU',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    __v: 0,
  },
];

// ================================
// UTILITY FUNCTIONS
// ================================

/**
 * Sanitizes phone number input by removing all non-numeric characters
 */
export const sanitizePhoneInput = (value: string): string => {
  return value.replace(/[^0-9]/g, '');
};

/**
 * Lookup maps for O(1) country resolution. Built once when countryCodes change.
 */
export type CountryLookup = {
  byCodeAndAbbr: Map<string, CountryCodes>;
  byCode: Map<string, CountryCodes[]>;
};

/**
 * Creates a memoizable lookup for fast country resolution (code, code+abbreviation).
 */
export const createCountryLookup = (
  countryCodes: CountryCodes[],
): CountryLookup => {
  const byCodeAndAbbr = new Map<string, CountryCodes>();
  const byCode = new Map<string, CountryCodes[]>();

  for (const country of countryCodes) {
    const key = `${country.code}:${country.abbreviation || ''}`;
    byCodeAndAbbr.set(key, country);

    if (!byCode.has(country.code)) {
      byCode.set(country.code, []);
    }
    byCode.get(country.code)!.push(country);
  }

  return { byCodeAndAbbr, byCode };
};

/**
 * Resolves a country using a pre-built lookup (O(1)). When abbreviation is provided,
 * tries exact match first, then first country with that code.
 */
export const findCountryByCodeFromLookup = (
  lookup: CountryLookup,
  code: string,
  abbreviation?: string,
): CountryCodes | undefined => {
  if (!code) return undefined;

  if (abbreviation != null && abbreviation !== '') {
    const key = `${code}:${abbreviation || ''}`;
    return lookup.byCodeAndAbbr.get(key) || lookup.byCode.get(code)?.[0];
  }
  return lookup.byCode.get(code)?.[0];
};

/**
 * Finds a country by user country shape. When abbreviation is provided, tries
 * exact match (code + abbreviation) first to handle shared codes (e.g. +1 for US vs CA),
 * then falls back to code-only for backward compatibility.
 */
export const findCountryByCode = (
  userCountry: UserCountryCode,
  countryCodes: CountryCodes[],
): CountryCodes | undefined => {
  if (!userCountry?.code) return undefined;

  const { code, abbreviation } = userCountry;
  if (abbreviation != null && abbreviation !== '') {
    const normalizedAbbreviation = abbreviation || '';
    return (
      countryCodes.find(
        c => c.code === code && c.abbreviation === normalizedAbbreviation,
      ) || countryCodes.find(c => c.code === code)
    );
  }
  return countryCodes.find(c => c.code === code);
};

/**
 * Finds a country code by country name
 */
export const findCountryCodeByName = (
  countryCodes: CountryCodes[],
  countryName: string,
): string | undefined => {
  const country = countryCodes.find(country => country.name === countryName);
  return country?.code;
};

/**
 * Validates phone number length using universal rules
 */
export const validatePhoneLength = (
  phoneNumber: string,
  countryCode: string,
  countryCodes: CountryCodes[],
): { isValid: boolean; error?: string } => {
  const country = findCountryByCode({ code: countryCode }, countryCodes);
  const countryName = country?.name || 'selected country';
  const length = phoneNumber.length;

  if (length < UNIVERSAL_PHONE_VALIDATION.minLength) {
    return {
      isValid: false,
      error: `Phone number must be at least ${UNIVERSAL_PHONE_VALIDATION.minLength} digits for ${countryName}`,
    };
  }

  if (length > UNIVERSAL_PHONE_VALIDATION.maxLength) {
    return {
      isValid: false,
      error: `Phone number cannot exceed ${UNIVERSAL_PHONE_VALIDATION.maxLength} digits for ${countryName}`,
    };
  }

  return { isValid: true };
};

/**
 * Length check for optional phone fields: blank stays valid, anything typed must be in range.
 */
export const isOptionalPhoneLengthValid = (phoneNumber: string): boolean => {
  const digits = sanitizePhoneInput(phoneNumber || '');
  return (
    digits.length === 0 ||
    (digits.length >= UNIVERSAL_PHONE_VALIDATION.minLength &&
      digits.length <= UNIVERSAL_PHONE_VALIDATION.maxLength)
  );
};

/**
 * Formats country code display with abbreviation
 */
export const formatCountryCodeDisplay = (
  countryCode: string,
  countryCodes: CountryCodes[],
): string => {
  const country = findCountryByCode({ code: countryCode }, countryCodes);
  return country ? `${countryCode} ${country.abbreviation}` : countryCode;
};

/**
 * Gets the maximum allowed phone number length
 */
export const getMaxPhoneLength = (): number =>
  UNIVERSAL_PHONE_VALIDATION.maxLength;

/**
 * Gets the phone validation message
 */
export const getValidationMessage = (): string =>
  i18n.t('validation.numbersOnlyRange', {
    ns: 'common',
    min: UNIVERSAL_PHONE_VALIDATION.minLength,
    max: UNIVERSAL_PHONE_VALIDATION.maxLength,
  });

// ================================
// STANDALONE UTILITY EXPORTS
// ================================

// Export utility functions for direct use if needed
export const phoneValidationUtils = {
  sanitizePhoneInput,
  findCountryByCode,
  findCountryCodeByName,
  validatePhoneLength,
  isOptionalPhoneLengthValid,
  getMaxPhoneLength,
  getValidationMessage,
  formatCountryCodeDisplay,
} as const;
